import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  Share2,
} from 'lucide-react';
import { siteSettingsService } from '../../services/siteSettingsService';

const TikTokIcon = ({ className = 'w-5 h-5', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    {...props}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const FooterColumn = ({ title, links, open, onToggle }) => (
  <div className="border-b border-white/10 md:border-b-0">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between py-4 md:block md:w-auto md:py-0"
    >
      <h3 className="mb-0 text-[13px] font-medium tracking-[0.04em] text-white/55 md:mb-5">
        {title}
      </h3>
      <span className="md:hidden">
        {open ? (
          <ChevronUp className="h-4 w-4 text-white/40" />
        ) : (
          <ChevronDown className="h-4 w-4 text-white/40" />
        )}
      </span>
    </button>
    <ul
      className={`space-y-3 overflow-hidden transition-all duration-300 ${
        open ? 'max-h-96 pb-4 md:pb-0' : 'max-h-0 md:max-h-96'
      }`}
    >
      {links.map((link) => (
        <li key={link.name}>
          <Link
            to={link.href}
            className="block text-[15px] font-normal text-white/70 transition-colors hover:text-white"
          >
            {link.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const ClientFooter = () => {
  const currentYear = new Date().getFullYear();
  const [openSections, setOpenSections] = useState({});

  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });
  const settings = settingsResponse?.data || {};

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const footerLinks = {
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'How It Works', href: '/how-it-works' },
      { name: 'Blog', href: '/blog' },
    ],
    support: [
      { name: 'Help Center', href: '/help' },
      { name: 'Contact Us', href: '/contact' },
      { name: 'Safety', href: '/safety' },
    ],
    vendors: [
      { name: 'Become a Vendor', href: '/register' },
      { name: 'Vendor Success Stories', href: '/vendor/success' },
    ],
    legal: [
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Privacy Policy', href: '/privacy' },
    ],
  };

  const iconMap = {
    Facebook,
    Twitter,
    Instagram,
    LinkedIn: Linkedin,
    YouTube: Youtube,
    TikTok: TikTokIcon,
    Pinterest: Share2,
  };
  const getIcon = (name) => iconMap[name] || Share2;

  const hasSettings = !!settingsResponse?.data;
  const socialLinks =
    hasSettings && settings.social_links?.length > 0
      ? settings.social_links
          .filter((s) => s.platform && s.url)
          .map((s) => ({
            name: s.platform,
            icon: getIcon(s.platform),
            href: s.url,
          }))
      : hasSettings
        ? [
            { name: 'Facebook', icon: Facebook, href: settings.facebook_url },
            { name: 'Twitter', icon: Twitter, href: settings.twitter_url },
            { name: 'Instagram', icon: Instagram, href: settings.instagram_url },
            { name: 'LinkedIn', icon: Linkedin, href: settings.linkedin_url },
            { name: 'YouTube', icon: Youtube, href: settings.youtube_url },
            { name: 'TikTok', icon: TikTokIcon, href: settings.tiktok_url },
            { name: 'Pinterest', icon: Share2, href: settings.pinterest_url },
          ].filter((s) => s.href)
        : [
    { name: 'Facebook', icon: Facebook, href: 'https://facebook.com' },
    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com' },
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com' },
  ];

  const siteName = settings.site_name || 'VendaList';

  return (
    <footer className="bg-black text-white">
      <div className="page-container">
        <div className="py-14 md:py-20">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-4">
            <div>
              <Link to="/" className="mb-5 inline-flex items-center">
                <img
                  src={settings.site_logo || '/images/VendaLOGO1.png'}
                  alt={siteName}
                  className="h-9 w-auto object-contain md:h-10"
                />
          </Link>
          
              <p className="mb-6 hidden max-w-xs text-[15px] font-normal leading-relaxed text-white/55 md:block">
                {settings.footer_tagline ||
                  'Connecting event organizers with verified vendors for photo booths, entertainment, and event services.'}
              </p>

              <div className="space-y-2">
                {(settings.site_email || !hasSettings) && (
                  <div className="flex items-center text-[14px] font-normal text-white/55">
                    <Mail className="mr-2.5 h-3.5 w-3.5 shrink-0" />
                    <span>{settings.site_email || 'vendalist@gmail.com'}</span>
                  </div>
                )}
                {(settings.site_phone || !hasSettings) && (
                  <div className="flex items-center text-[14px] font-normal text-white/55">
                    <Phone className="mr-2.5 h-3.5 w-3.5 shrink-0" />
                    <span>{settings.site_phone || '1-800-PHOTO-BOOTH'}</span>
              </div>
                )}
            </div>
          </div>

            <FooterColumn
              title="Company"
              links={footerLinks.company}
              open={!!openSections.company}
              onToggle={() => toggleSection('company')}
            />
            <FooterColumn
              title="Support"
              links={footerLinks.support}
              open={!!openSections.support}
              onToggle={() => toggleSection('support')}
            />
            <FooterColumn
              title="For Vendors"
              links={footerLinks.vendors}
              open={!!openSections.vendors}
              onToggle={() => toggleSection('vendors')}
            />
          </div>
        </div>

        <div className="border-t border-white/10 py-6 md:py-7">
          <div className="flex flex-col space-y-5 md:hidden">
            <div className="flex justify-center gap-5">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/45 transition-colors hover:text-white"
                  aria-label={social.name}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            <div className="text-center text-[12px] font-normal text-white/40">
              {settings.footer_copyright ||
                `© ${currentYear} ${siteName}. All rights reserved.`}
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-[12px] font-normal">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-white/45 transition-colors hover:text-white"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          
          <div className="hidden items-center justify-between md:flex">
            <div className="text-[13px] font-normal text-white/40">
              {settings.footer_copyright ||
                `© ${currentYear} ${siteName}. All rights reserved.`}
            </div>
            <div className="flex gap-5">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                  className="text-white/45 transition-colors hover:text-white"
                aria-label={social.name}
              >
                  <social.icon className="h-4 w-4" />
              </a>
            ))}
          </div>
            <div className="flex gap-6 text-[13px] font-normal">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                  className="text-white/45 transition-colors hover:text-white"
              >
                {link.name}
              </Link>
            ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ClientFooter;
